import { debounce } from 'lodash';
import ContactsUnlinked from '../../store/collections/ContactsUnlinked';
import ContactsDuplicate from '../../store/collections/ContactsDuplicate';
import { baseApi } from 'core/api';

const state = () => ({
    _duplicateCount: 0,
    _duplicateItems: [],
    _duplicatePagination: {},
    _duplicateParameters: {
        page: 1,
        length: 10,
    },
    _duplicateLoading: false,
    _duplicateLoadingError: false,

    _unlinkedCount: 0,
    _unlinkedItems: [],
    _unlinkedPagination: {},
    _unlinkedParameters: {
        page: 1,
        length: 10,
    },
    _unlinkedLoading: false,
    _unlinkedLoadingError: false,
});

const getters = {
    contactsDuplicate: (state) => state._duplicateItems,
    contactsDuplicateCount: (state) => state._duplicateCount,
    contactsDuplicatePagination: (state) => state._duplicatePagination,
    contactsDuplicateParameters: (state) => state._duplicateParameters,
    contactsDuplicateLoading: (state) => state._duplicateLoading,
    contactsDuplicateLoadingError: (state) => state._duplicateLoadingError,

    contactsUnlinked: (state) => state._unlinkedItems,
    contactsUnlinkedCount: (state) => state._unlinkedCount,
    contactsUnlinkedPagination: (state) => state._unlinkedPagination,
    contactsUnlinkedParameters: (state) => state._unlinkedParameters,
    contactsUnlinkedLoading: (state) => state._unlinkedLoading,
    contactsUnlinkedLoadingError: (state) => state._unlinkedLoadingError,

    hasContactsCountDuplicateAndUnlinked: (state) =>
        Boolean(state._duplicateCount || state._unlinkedCount),
};

const utils = {
    contactsUnlinkedLoad({ loading }) {
        return baseApi
            .get('core/contacts/merge/unlinked', {
                params: this._unlinkedParameters,
            })
            .then((response) => {
                const data = response.data;

                this._setContactsMergeUnlinkedLoaded(data);

                return data;
            })
            .catch(() => {
                this._setContactsMergeUpdateState({ unlinkedLoadingError: true });
            })
            .finally(() => {
                if (loading) this._setContactsMergeUpdateState({ _unlinkedLoading: false });
            });
    },

    contactsDuplicateLoad({ loading }) {
        return baseApi
            .get('core/contacts/merge/duplicate', {
                params: this._duplicateParameters,
            })
            .then((response) => {
                const data = response.data;

                this._setContactsMergeDuplicateLoaded(data);

                return data;
            })
            .catch(() => {
                this._setContactsMergeUpdateState({ duplicateLoadingError: true });
            })
            .finally(() => {
                if (loading) this._setContactsMergeUpdateState({ _duplicateLoading: false });
            });
    },
};

// throttle actions - these will be bound to store context when called
const debounceContactsUnlinkedLoad = debounce(utils.contactsUnlinkedLoad, 500);
const debounceContactsDuplicateLoad = debounce(utils.contactsDuplicateLoad, 500);

const actions = {
    getContactsMergeAllCount() {
        return baseApi.get('core/contacts/merge/all/count').then((response) => {
            const data = response.data;

            this._setContactsMergeCount(data);

            return data;
        });
    },

    getContactsMergeDuplicatePair({ params = {}, isAxios = true, form = null } = {}) {
        if (!isAxios) {
            return form.get('core/contacts/merge/duplicate/pair').then((response) => {
                const data = response.data;

                return data;
            });
        }

        return baseApi.get('core/contacts/merge/duplicate/pair', { params }).then((response) => {
            const data = response.data;

            return data;
        });
    },

    updateContactsMergeDuplicateResolve({ params = {} } = {}) {
        return baseApi.post('core/contacts/merge/duplicate/resolve', params).then((response) => {
            const data = response.data;

            return data;
        });
    },

    contactsDuplicateLoad({ loading = true, debounce = true } = {}) {
        if (loading) this._setContactsMergeUpdateState({ _duplicateLoading: true });

        if (debounce) {
            return debounceContactsDuplicateLoad.call(this, { loading });
        }

        return utils.contactsDuplicateLoad.call(this, { loading });
    },

    contactsDuplicateUpdatePage(page) {
        this._setContactsMergeDuplicateUpdatePage(page);

        return this.contactsDuplicateLoad();
    },

    contactsDuplicateUpdatePageCustom({ page, loading, debounce } = {}) {
        this._setContactsMergeDuplicateUpdatePage(page);

        return this.contactsDuplicateLoad({ loading, debounce });
    },

    contactsDuplicateUpdateLength(length) {
        this._setContactsMergeDuplicateUpdateLength(length);

        return this.contactsDuplicateLoad();
    },

    contactsUnlinkedLoad({ loading = true } = {}) {
        if (loading) this._setContactsMergeUpdateState({ _unlinkedLoading: true });

        return debounceContactsUnlinkedLoad.call(this, { loading });
    },

    contactsUnlinkedLoadList() {
        return baseApi
            .get('core/contacts/merge/unlinked', {
                params: {
                    is_pagination: 0,
                },
            })
            .then((response) => {
                return new ContactsUnlinked(response.data).models;
            });
    },

    contactsUnlinkedUpdatePage(page) {
        this._setContactsMergeUnlinkedUpdatePage(page);

        return this.contactsUnlinkedLoad();
    },

    contactsUnlinkedUpdateLength(length) {
        this._setContactsMergeUnlinkedUpdateLength(length);

        return this.contactsUnlinkedLoad();
    },

    contactsMergeUpdateState(props) {
        this._setContactsMergeUpdateState(props);
    },

    contactsDuplicateMergeReset() {
        this._setContactsMergeUpdateState({
            _duplicateItems: [],
            _duplicatePagination: {},
            _duplicateLoadingError: false,
            _duplicateLoading: false,
            _duplicateParameters: {
                page: 1,
                length: 10,
            },
        });
    },

    contactsUnlinkedMergeReset() {
        this._setContactsMergeUpdateState({
            _unlinkedItems: [],
            _unlinkedPagination: {},
            _unlinkedLoadingError: false,
            _unlinkedLoading: false,
            _unlinkedParameters: {
                page: 1,
                length: 10,
            },
        });
    },

    // Private methods
    _setContactsMergeUpdateState(props) {
        for (let key in props) {
            this[key] = props[key];
        }
    },

    _setContactsMergeCount(data) {
        this._duplicateCount = data.duplicate_count;
        this._unlinkedCount = data.unlinked_count;
    },

    _setContactsMergeDuplicateLoaded(data) {
        const items = new ContactsDuplicate(data.data).models;

        this._duplicateItems = items;
        this._duplicatePagination = {
            type: '',
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            hasNext: data.next_page_url,
        };
        this._duplicateLoadingError = false;
    },

    _setContactsMergeDuplicateUpdatePage(data) {
        this._duplicateParameters.page = data;
    },

    _setContactsMergeDuplicateUpdateLength(data) {
        this._duplicateParameters.page = 1;
        this._duplicateParameters.length = data;
    },

    _setContactsMergeUnlinkedLoaded(data) {
        this._unlinkedItems = new ContactsUnlinked(data.data).models;
        this._unlinkedPagination = {
            type: '',
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            hasNext: data.next_page_url,
        };
        this._unlinkedLoadingError = false;
    },

    _setContactsMergeUnlinkedUpdatePage(data) {
        this._unlinkedParameters.page = data;
    },

    _setContactsMergeUnlinkedUpdateLength(data) {
        this._unlinkedParameters.page = 1;
        this._unlinkedParameters.length = data;
    },
};

export default {
    state,
    getters,
    actions,
};
